/**
 * CredencialesAppPanel — gestión de credenciales del asociado para login en la app.
 *
 * Encapsula las 4 acciones de la edge function `admin-credentials`:
 *   - invite:  primer PIN (crea auth.user con phone + PIN)
 *   - reset:   nuevo PIN (invalida el anterior)
 *   - disable: banea login en auth
 *   - unlink:  borra auth.user (deja asociado huérfano)
 *
 * Incluye el modal de PIN que se muestra una sola vez con botón
 * "Abrir WhatsApp" pre-cargado con mensaje al telefono del asociado.
 *
 * Reusable: lo monta tanto AsociadosPage como NeoCoreAsociadoFichaPage.
 */

import { useState } from 'react'
import { supabase } from '@/lib/supabase'

export type AppStatus =
  | 'no_invitado'
  | 'invitado'
  | 'invite_expirado'
  | 'activo'
  | 'bloqueado'
  | 'desconocido'

export type ActionResult = {
  ok?: boolean
  pin?: string
  no_asociado: string
  error?: string
  detail?: string
}

type Props = {
  asociadoId: string
  noAsociado: string
  nombreCompleto: string
  appStatus: AppStatus
  telefonoPreferido: string | null  // telefono_app ?? telefono_e164
  inviteSentAt?: string | null
  onActionComplete?: (res?: ActionResult) => void
}

export function CredencialesAppPanel({
  asociadoId,
  noAsociado,
  nombreCompleto,
  appStatus,
  telefonoPreferido,
  inviteSentAt,
  onActionComplete,
}: Props) {
  const [busy, setBusy] = useState<string | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [pinDialog, setPinDialog] = useState<ActionResult | null>(null)

  const call = async (
    action: 'invite' | 'reset' | 'disable' | 'unlink',
    confirmMsg?: string,
  ) => {
    if (confirmMsg && !confirm(confirmMsg)) return
    setBusy(action)
    setErr(null)
    try {
      const { data, error } = await supabase.functions.invoke('admin-credentials', {
        body: {
          action,
          asociado_id: asociadoId,
          contacto: telefonoPreferido,
        },
      })
      if (error) throw new Error(error.message)
      const res = data as ActionResult
      if (res.error) {
        if (res.error === 'telefono_ocupado') {
          throw new Error(res.detail ?? 'Ese teléfono ya está ocupado por otro asociado.')
        }
        if (res.error === 'phone_required') {
          throw new Error(res.detail ?? 'Capturá primero el teléfono del asociado.')
        }
        if (res.error === 'already_invited') {
          throw new Error(res.detail ?? 'El asociado ya tiene credencial. Usá Reset.')
        }
        throw new Error(`${res.error}: ${res.detail ?? ''}`)
      }
      // Si la acción devolvió un PIN, mostrar el dialog
      if (res.pin) {
        setPinDialog(res)
      }
      onActionComplete?.(res)
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : 'Error desconocido')
    } finally {
      setBusy(null)
    }
  }

  const sinTelefono = !telefonoPreferido
  const puedeInvitar = appStatus === 'no_invitado' || appStatus === 'invite_expirado'
  const puedeReset = appStatus === 'invitado' || appStatus === 'activo'
  const puedeBloquear = appStatus === 'invitado' || appStatus === 'activo'
  const puedeDesbloquear = appStatus === 'bloqueado'
  const puedeDesvincular = appStatus !== 'no_invitado'

  return (
    <div className="card" style={{ padding: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        <h3 style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink)', margin: 0 }}>
          Credenciales de la app
        </h3>
        <StatusBadge status={appStatus} />
      </div>

      <div style={{ fontSize: 12, color: 'var(--muted-foreground)', marginBottom: 14, lineHeight: 1.5 }}>
        {appStatus === 'no_invitado' && (
          <>El asociado todavía no tiene acceso a la app. Generá su primer PIN y se lo enviás por WhatsApp.</>
        )}
        {appStatus === 'invitado' && inviteSentAt && (
          <>Invitación enviada el {new Date(inviteSentAt).toLocaleDateString('es-GT', { timeZone: 'America/Guatemala' })}. Si no la recibió, podés reenviar el PIN.</>
        )}
        {appStatus === 'invite_expirado' && (
          <>El PIN de la invitación caducó. Generá uno nuevo.</>
        )}
        {appStatus === 'activo' && (
          <>El asociado ya entró a la app y configuró su contraseña. Solo resetealo si la olvidó.</>
        )}
        {appStatus === 'bloqueado' && (
          <>Acceso bloqueado. Reseteá el PIN para reactivarlo.</>
        )}
      </div>

      {sinTelefono && (
        <div
          style={{
            padding: '10px 14px',
            background: 'rgba(217, 119, 6, 0.08)',
            border: '1px solid rgba(217, 119, 6, 0.22)',
            borderRadius: 'var(--radius-md)',
            fontSize: 12,
            color: 'var(--warning)',
            marginBottom: 12,
          }}
        >
          Capturá el teléfono del asociado antes de invitar. Sin teléfono no podemos generar credenciales.
        </div>
      )}

      {err && (
        <div
          style={{
            padding: '10px 14px',
            background: 'rgba(220, 38, 38, 0.06)',
            border: '1px solid rgba(220, 38, 38, 0.18)',
            borderRadius: 'var(--radius-md)',
            fontSize: 12,
            color: 'var(--error)',
            marginBottom: 12,
          }}
        >
          {err}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {puedeInvitar && (
          <button
            className="btn btn-primary"
            onClick={() => call('invite')}
            disabled={busy !== null || sinTelefono}
          >
            {busy === 'invite' ? 'Generando PIN…' : 'Invitar — generar PIN'}
          </button>
        )}
        {puedeReset && (
          <button
            className="btn btn-ghost"
            onClick={() => call('reset', '¿Generar un nuevo PIN? El anterior deja de funcionar.')}
            disabled={busy !== null || sinTelefono}
          >
            {busy === 'reset' ? 'Generando…' : 'Reenviar / Reset PIN'}
          </button>
        )}
        {puedeBloquear && (
          <button
            className="btn btn-ghost"
            onClick={() =>
              call(
                'disable',
                '¿Bloquear acceso a la app? El asociado no podrá entrar hasta que lo reactives con un reset.',
              )
            }
            disabled={busy !== null}
            style={{ color: 'var(--error)' }}
          >
            {busy === 'disable' ? 'Bloqueando…' : 'Bloquear acceso'}
          </button>
        )}
        {puedeDesbloquear && (
          <button
            className="btn btn-primary"
            onClick={() => call('reset', '¿Generar un nuevo PIN y desbloquear?')}
            disabled={busy !== null || sinTelefono}
          >
            {busy === 'reset' ? 'Desbloqueando…' : 'Desbloquear con nuevo PIN'}
          </button>
        )}
        {puedeDesvincular && (
          <button
            className="btn btn-ghost"
            onClick={() =>
              call(
                'unlink',
                '¿Desvincular del todo? Borra el usuario de auth y deja al asociado sin credenciales.',
              )
            }
            disabled={busy !== null}
            style={{ color: 'var(--n-500)', marginLeft: 'auto' }}
          >
            {busy === 'unlink' ? 'Desvinculando…' : 'Desvincular'}
          </button>
        )}
      </div>

      {pinDialog && pinDialog.pin && (
        <PinDialog
          pin={pinDialog.pin}
          noAsociado={pinDialog.no_asociado || noAsociado}
          nombreCompleto={nombreCompleto}
          contacto={telefonoPreferido}
          onClose={() => setPinDialog(null)}
        />
      )}
    </div>
  )
}

// -----------------------------------------------------------------------------
// StatusBadge
// -----------------------------------------------------------------------------

function StatusBadge({ status }: { status: AppStatus }) {
  const map: Record<AppStatus, { label: string; color: string; bg: string }> = {
    no_invitado: { label: 'No invitado', color: 'var(--n-600)', bg: 'var(--n-100)' },
    invitado: { label: 'Invitado', color: 'var(--brand-700)', bg: 'var(--brand-50)' },
    invite_expirado: { label: 'Invite expirado', color: 'var(--warning)', bg: '#fef3c7' },
    activo: { label: 'Activo', color: 'var(--success)', bg: '#dcfce7' },
    bloqueado: { label: 'Bloqueado', color: 'var(--error)', bg: '#fee2e2' },
    desconocido: { label: '—', color: 'var(--n-500)', bg: 'var(--n-100)' },
  }
  const s = map[status]
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 600,
        color: s.color,
        background: s.bg,
      }}
    >
      {s.label}
    </span>
  )
}

// -----------------------------------------------------------------------------
// PinDialog — modal que muestra el PIN UNA SOLA VEZ + abre WhatsApp con
// mensaje pre-armado. Si el admin lo cierra sin copiar, no hay forma de
// volver a verlo, tiene que generar uno nuevo con Reset.
// -----------------------------------------------------------------------------

function PinDialog({
  pin,
  noAsociado,
  nombreCompleto,
  contacto,
  onClose,
}: {
  pin: string
  noAsociado: string
  nombreCompleto: string
  contacto: string | null
  onClose: () => void
}) {
  const [copied, setCopied] = useState(false)
  const [phone, setPhone] = useState(contacto ?? '')

  const copyPin = async () => {
    await navigator.clipboard.writeText(pin)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const firstName = (() => {
    const parts = nombreCompleto.trim().split(/\s+/)
    const first = parts[0] ?? ''
    return first ? first.charAt(0).toUpperCase() + first.slice(1).toLowerCase() : ''
  })()

  const digits = phone.replace(/\D/g, '')
  // Mensaje cordial de bienvenida. Incluye link a neocoop.gt/app para
  // que el asociado pueda descargar la app (iOS/Android) desde una sola
  // URL, y las credenciales de acceso destacadas.
  const message =
    `¡Hola ${firstName}! 👋\n\n` +
    `Gracias por ser parte de Neocoop 💫\n\n` +
    `Ya podés acceder a tu cuenta desde nuestra app móvil. Descargala en:\n` +
    `👉 https://neocoop.gt/app\n\n` +
    `Tus datos para ingresar:\n` +
    `📱 Usuario: tu número de teléfono\n` +
    `🔐 Contraseña temporal: ${pin}\n\n` +
    `La primera vez que ingreses, la app te va a pedir crear tu contraseña personal.\n\n` +
    `Si tenés dudas, estamos acá para ayudarte. ¡Bienvenido/a a tu cooperativa! 🧡`
  const waHref = digits.length >= 8 ? `https://wa.me/${digits}?text=${encodeURIComponent(message)}` : null

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.4)',
        zIndex: 50,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--surface)',
          borderRadius: 12,
          padding: 24,
          maxWidth: 480,
          width: '100%',
          boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
        }}
      >
        <h3 style={{ margin: 0, fontSize: 16 }}>PIN generado</h3>
        <p style={{ color: 'var(--n-500)', fontSize: 13, margin: '6px 0 16px', lineHeight: 1.5 }}>
          Este PIN se muestra <strong>una sola vez</strong>. Compartilo con el asociado por
          WhatsApp. Si cerrás sin copiarlo, no vas a poder volver a verlo — tenés que generar
          uno nuevo con "Reset PIN".
        </p>

        <div
          style={{
            background: 'var(--bg)',
            border: '2px solid var(--brand-500)',
            borderRadius: 12,
            padding: 16,
            textAlign: 'center',
            margin: '12px 0 16px',
          }}
        >
          <div style={{ color: 'var(--n-500)', fontSize: 11, marginBottom: 6 }}>
            Usuario: <span className="mono">{noAsociado}</span>
          </div>
          <div
            className="mono"
            style={{
              fontSize: 32,
              letterSpacing: 6,
              fontWeight: 700,
              color: 'var(--brand-600)',
            }}
          >
            {pin}
          </div>
        </div>

        <label style={{ display: 'block', fontSize: 12, color: 'var(--n-500)', marginBottom: 4 }}>
          WhatsApp al que se envía
        </label>
        <input
          type="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="+50258658811"
          style={{
            width: '100%',
            border: '1px solid var(--n-200)',
            borderRadius: 8,
            padding: '8px 12px',
            fontSize: 14,
            fontFamily: 'ui-monospace, monospace',
            marginBottom: 4,
          }}
        />
        <div style={{ fontSize: 11, color: 'var(--n-500)', marginBottom: 14 }}>
          {contacto
            ? `Default: teléfono del asociado (${contacto}). Editalo si vas a mandarlo por otra línea.`
            : 'Este asociado no tiene teléfono en NeoCore — ponelo a mano.'}
        </div>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn btn-ghost" onClick={copyPin} style={{ flex: 1 }}>
            {copied ? '✓ Copiado' : 'Copiar PIN'}
          </button>
          {waHref ? (
            <a
              href={waHref}
              target="_blank"
              rel="noreferrer"
              className="btn btn-primary"
              style={{ flex: 1, textDecoration: 'none', textAlign: 'center' }}
            >
              Abrir WhatsApp
            </a>
          ) : (
            <button className="btn btn-primary" disabled style={{ flex: 1 }}>
              Abrir WhatsApp (falta teléfono)
            </button>
          )}
        </div>

        <button
          className="btn btn-ghost"
          onClick={onClose}
          style={{ marginTop: 8, width: '100%' }}
        >
          Cerrar
        </button>
      </div>
    </div>
  )
}
